import type {
	HideableTextShape,
	LabelProps,
	Selectable,
} from "@aerstudios/aerated-types/types";
import { type ReactElement, isValidElement } from "react";

export const removeEmptyObjectKVs = (
	originalObject: Record<string | number | symbol, unknown | undefined>,
): Record<string | number | symbol, unknown> => {
	const obj = JSON.parse(JSON.stringify(originalObject));

	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			const element = obj[key];

			if (typeof element === "undefined") {
				delete obj[key];
			}
		}
	}

	return obj;
};

export const elementIsHideableTextShape = (
	element: unknown,
): element is HideableTextShape => {
	return isObject(element) && "hide" in element && "text" in element;
};

export const hasChildren = (element: React.ReactElement) =>
	isValidElement(element) &&
	Boolean((element as React.ReactElement).props.children);

export const getOnlyTextContent = (
	element: React.ReactElement | string,
): string => {
	if (!element) {
		return "";
	}
	if (typeof element === "string") {
		return element;
	}
	const children = element.props?.children;

	if (Array.isArray(children)) {
		return children.map(getOnlyTextContent).join("");
	}
	return getOnlyTextContent(children);
};

// biome-ignore lint/complexity/noBannedTypes: <I don't think this rule applies here>
export const isObject = (obj: unknown): obj is Object => {
	return !!obj && typeof obj === "object" && obj.constructor === Object;
};

export const implementsTheSelectableInterface = (
	obj: unknown,
): obj is Selectable =>
	isObject(obj) &&
	"value" in obj &&
	("label" in obj || "checked" in obj || "defaultChecked" in obj);

export const isLabelProps = (
	props: string | ReactElement | LabelProps,
): props is LabelProps => typeof props === "object" && "hidden" in props;
